import { toDisplayString } from "./QuestionField";
import { isAnswered } from "../SharedProfile/showIf";

export type PrevVisit = {
  tp: string;
  date: string;
  value: string;
};

/** Stable id for a coach session row (API sends `sessionId`, older rows `id`). */
export const sessionKey = (s: any) => String(s?.sessionId || s?.id || "");

/** Visit number: 0 = Baseline, n >= 1 = Follow-up n; null when not numeric. */
export const sessionCount = (s: any): number | null =>
  typeof s?.assessmentCount === "number" && Number.isFinite(s.assessmentCount)
    ? s.assessmentCount
    : null;

/**
 * Which session of a visit to open: an unfinished draft/preview first,
 * otherwise the newest row (the API lists sessions oldest first).
 */
export const pickPreferredSession = (rows: any[]): any | null =>
  rows.find((s) => s.action === "draft" || s.action === "preview") ||
  rows[rows.length - 1] ||
  null;

export const formatVisitDate = (s: any): string => {
  const raw = s?.completedAt || s?.createdAt || s?.date || s?.updatedAt;
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const sessionOccurrenceLabel = (s: any): string => {
  if (s?.occurrence) return String(s.occurrence);
  if (s?.label) return String(s.label);
  const count = sessionCount(s);
  if (count === 0) return "Baseline";
  if (count != null) return `Follow-up ${count}`;
  return "Session";
};

const rawAnswerText = (raw: unknown): string => {
  if (raw == null) return "";
  if (Array.isArray(raw)) return toDisplayString(raw);
  return toDisplayString(raw);
};

/** Latest prior follow-up (if any) then Baseline — only visits with a real answer. */
export const prevVisitsForQuestion = (
  sessions: any[],
  activeSessionId: string | null,
  questionId: string,
): PrevVisit[] => {
  if (!questionId || !sessions?.length) return [];
  const active =
    sessions.find((s) => sessionKey(s) === String(activeSessionId || "")) ||
    null;
  const currentCount = sessionCount(active);
  if (currentCount == null || currentCount <= 0) return [];

  const baseline = sessions.find((s) => {
    if (sessionKey(s) === sessionKey(active)) return false;
    return sessionCount(s) === 0;
  });

  const priorFollowUps = sessions
    .filter((s) => {
      if (sessionKey(s) === sessionKey(active)) return false;
      const n = sessionCount(s);
      return n != null && n > 0 && n < currentCount;
    })
    .sort((a, b) => (sessionCount(b) || 0) - (sessionCount(a) || 0));
  const latestFu = priorFollowUps[0] || null;

  const rowFor = (s: any): PrevVisit | null => {
    const answers = s?.answers || {};
    const raw = answers[questionId];
    if (!isAnswered(raw)) return null;
    const value = rawAnswerText(raw);
    if (!value) return null;
    return {
      tp: sessionOccurrenceLabel(s),
      date: formatVisitDate(s),
      value,
    };
  };

  const out: PrevVisit[] = [];
  const fu = latestFu ? rowFor(latestFu) : null;
  const base = baseline ? rowFor(baseline) : null;
  if (fu) out.push(fu);
  if (base) out.push(base);
  return out;
};

export const formatOptionValue = (question: any, raw: string) => {
  if (!question || raw === "—" || raw === "") return raw;
  const options = question.options || [];
  if (!options.length) return raw;
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  const mapped = parts.map((part) => {
    const hit = options.find(
      (o: any) =>
        String(o.id) === part ||
        String(o.label) === part ||
        String(o.emoji) === part,
    );
    return hit?.label || part;
  });
  return mapped.join(", ");
};
